import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  MessageCircle, 
  Heart, 
  Share2, 
  Pin, 
  Loader2, 
  Send,
  Trash2,
  Image as ImageIcon,
  Video,
  Lock,
  Sparkles
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { API } from '../config/api';
import { canComment, formatRemainingComments, isCreatorRole, isFullTier } from '../utils/permissions';
import { safeJson } from '../utils/safeJson';



export function ArtistPostsFeed({ artistId, isOwnProfile = false }) {
  const { user, isAuthenticated, isArtist, getAuthHeaders } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [submittingComment, setSubmittingComment] = useState(null);
  const [commentStatus, setCommentStatus] = useState(null);

  useEffect(() => {
    fetchPosts();
    if (isAuthenticated) {
      fetchCommentStatus();
    }
  }, [artistId, isAuthenticated]);

  const fetchCommentStatus = async () => {
    try {
      const response = await fetch(`${API}/me/comment-status`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCommentStatus(data);
      }
    } catch (error) {
      console.error('Error fetching comment status:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API}/artists/${artistId}/posts`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (postId) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to like posts');
      return;
    }

    try {
      const response = await fetch(`${API}/artist/posts/${postId}/like`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(posts.map(p => 
          p.post_id === postId 
            ? { ...p, likes_count: p.likes_count + (data.liked ? 1 : -1) }
            : p
        ));
      }
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  const handleShare = (post) => {
    const url = `${window.location.origin}/artist/${artistId}?post=${post.post_id}`;
    try { navigator.clipboard.writeText(url).catch(() => {}); } catch {}
    toast.success('Link copied to clipboard!');
  };

  const toggleComments = async (postId) => {
    if (expandedComments[postId]) {
      setExpandedComments(prev => ({ ...prev, [postId]: null }));
    } else {
      try {
        const response = await fetch(`${API}/posts/${postId}/comments`);
        if (response.ok) {
          const comments = await response.json();
          setExpandedComments(prev => ({ ...prev, [postId]: comments }));
        }
      } catch (error) {
        console.error('Error fetching comments:', error);
      }
    }
  };

  const handleSubmitComment = async (postId) => {
    const text = commentText[postId]?.trim();
    if (!text) return;

    if (!isAuthenticated) {
      toast.error('Please sign in to comment');
      return;
    }

    // Use centralized permission check
    const commentPerm = canComment(user, commentStatus);
    if (!commentPerm.allowed) {
      if (commentPerm.rateLimited && commentStatus?.remaining === 0) {
        toast.error(commentPerm.reason || 'Comment limit reached. Upgrade to Pro for unlimited comments.');
      } else {
        toast.error(commentPerm.reason || 'You cannot comment at this time');
      }
      return;
    }

    setSubmittingComment(postId);

    try {
      const response = await fetch(`${API}/posts/${postId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ comment_text: text })
      });

      const result = await safeJson(response);

      if (result.ok && result.data) {
        setExpandedComments(prev => ({
          ...prev,
          [postId]: [result.data, ...(prev[postId] || [])]
        }));
        setPosts(posts.map(p => 
          p.post_id === postId 
            ? { ...p, comments_count: p.comments_count + 1 }
            : p
        ));
        setCommentText(prev => ({ ...prev, [postId]: '' }));
        toast.success('Comment posted!');
        
        // Refresh comment status after posting
        fetchCommentStatus();
      } else if (response.status === 429) {
        // Rate limit exceeded
        toast.error(result.error || 'Comment limit reached. Upgrade to Pro for unlimited comments.');
        fetchCommentStatus();
      } else {
        toast.error(result.error || 'Failed to post comment');
      }
    } catch (error) {
      toast.error('Failed to post comment');
    } finally {
      setSubmittingComment(null);
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    try {
      const response = await fetch(`${API}/comments/${commentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setExpandedComments(prev => ({
          ...prev,
          [postId]: prev[postId].filter(c => c.comment_id !== commentId)
        }));
        setPosts(posts.map(p => 
          p.post_id === postId 
            ? { ...p, comments_count: Math.max(0, p.comments_count - 1) }
            : p
        ));
        toast.success('Comment deleted');
      }
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No posts yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="artist-posts-feed">
      {posts.map((post) => (
        <Card key={post.post_id} className="p-4" data-testid={`post-${post.post_id}`}>
          {/* Post Header */}
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-muted overflow-hidden">
              {post.artist_avatar ? (
                <img src={post.artist_avatar} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  {post.artist_name?.charAt(0) || '?'}
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{post.artist_name}</span>
                {post.is_pinned && (
                  <Badge variant="secondary" className="text-xs">
                    <Pin className="w-3 h-3 mr-1" /> Pinned
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
              </p>
            </div>
          </div>

          {/* Post Content */}
          <p className="text-sm mb-3 whitespace-pre-wrap">{post.content_text}</p>

          {/* Post Image */}
          {post.content_image_url && (
            <div className="mb-3 rounded-lg overflow-hidden">
              <img 
                src={post.content_image_url} 
                alt="" 
                className="w-full max-h-96 object-cover"
              />
            </div>
          )}

          {/* Post Video */}
          {post.content_video_url && (
            <div className="mb-3 rounded-lg overflow-hidden aspect-video">
              <iframe
                src={post.content_video_url}
                className="w-full h-full"
                allowFullScreen
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-4 pt-2 border-t border-border">
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-muted-foreground hover:text-red-400"
              onClick={() => handleLike(post.post_id)}
            >
              <Heart className="w-4 h-4 mr-1" />
              {post.likes_count || 0}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-muted-foreground"
              onClick={() => toggleComments(post.post_id)}
            >
              <MessageCircle className="w-4 h-4 mr-1" />
              {post.comments_count || 0}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-muted-foreground"
              onClick={() => handleShare(post)}
            >
              <Share2 className="w-4 h-4 mr-1" />
              Share
            </Button>
          </div>

          {/* Comments Section */}
          {expandedComments[post.post_id] !== undefined && (
            <div className="mt-4 pt-4 border-t border-border space-y-3">
              {/* Comment Input - Only for non-artists */}
              {isAuthenticated && !isArtist ? (
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Write a comment..."
                    value={commentText[post.post_id] || ''}
                    onChange={(e) => setCommentText(prev => ({ ...prev, [post.post_id]: e.target.value }))}
                    className="min-h-[60px] text-sm"
                    maxLength={250}
                  />
                  <Button 
                    size="icon"
                    onClick={() => handleSubmitComment(post.post_id)}
                    disabled={submittingComment === post.post_id || !commentText[post.post_id]?.trim()}
                  >
                    {submittingComment === post.post_id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              ) : isArtist ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 rounded p-2">
                  <Lock className="w-3 h-3" />
                  Artists cannot comment. Only listeners can leave comments.
                </div>
              ) : null}

              {/* Comments List */}
              {expandedComments[post.post_id]?.length > 0 ? (
                <div className="space-y-3">
                  {expandedComments[post.post_id].map((comment) => (
                    <div key={comment.comment_id} className="flex gap-2">
                      <div className="w-8 h-8 rounded-full bg-muted overflow-hidden flex-shrink-0">
                        {comment.user_avatar ? (
                          <img src={comment.user_avatar} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                            {comment.user_name?.charAt(0) || '?'}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 bg-muted/30 rounded-lg p-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium">{comment.user_name}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                            </span>
                            {user?.user_id === comment.user_id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-red-400 hover:text-red-300"
                                onClick={() => handleDeleteComment(post.post_id, comment.comment_id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <p className="text-sm mt-1">{comment.comment_text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground text-center py-2">No comments yet</p>
              )}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
